import type { InspectorContext, InspectorEntry, InspectorStateValue } from './protocol';

export const formatInspectorState = (value: InspectorStateValue): string => {
  if (typeof value === 'string') return value;

  return Object.entries(value)
    .map(([region, nestedValue]) => `${region}.${formatInspectorState(nestedValue)}`)
    .join(' | ');
};

export const formatInspectorDelta = (ms: number) =>
  ms < 1000 ? `+${ms}ms` : `+${(ms / 1000).toFixed(1)}s`;

export interface InspectorRegionCell {
  changed: boolean;
  region: string;
  value: string;
}

/** Column keys for a parallel machine; empty when every snapshot is a scalar state. */
export const getInspectorRegionKeys = (entries: InspectorEntry[]): string[] => {
  const keys = new Set<string>();

  for (const entry of entries) {
    if (typeof entry.state === 'string') continue;
    for (const region of Object.keys(entry.state)) keys.add(region);
  }

  return [...keys];
};

/**
 * `previous` is passed in rather than read off an entry so callers choose what "before" means:
 * the events table diffs against the previous row, the details panel against previousState.
 */
export const getInspectorRegionCells = (
  state: InspectorStateValue,
  previous: InspectorStateValue | undefined,
  regionKeys: string[],
): InspectorRegionCell[] => {
  const current = typeof state === 'string' ? undefined : state;
  const before = previous !== undefined && typeof previous !== 'string' ? previous : undefined;

  return regionKeys.map((region) => {
    const regionValue = current?.[region];
    const value = regionValue === undefined ? '' : formatInspectorState(regionValue);
    const previousValue = before?.[region];

    return {
      changed: previousValue !== undefined && formatInspectorState(previousValue) !== value,
      region,
      value,
    };
  });
};

export interface InspectorContextChange {
  field: keyof InspectorContext;
  from: InspectorContext[keyof InspectorContext] | undefined;
  to: InspectorContext[keyof InspectorContext];
}

export const getInspectorContextChanges = (
  current: InspectorContext,
  previous?: InspectorContext,
): InspectorContextChange[] =>
  (Object.keys(current) as Array<keyof InspectorContext>)
    .filter((field) => previous?.[field] !== current[field])
    .map((field) => ({ field, from: previous?.[field], to: current[field] }));
